<template>
  <div>
    <div
      v-if="loaded && withFilters"
      class="top-content-bar">
      <div class="wrapper pt-3 pb-1">
        <validation-observer
          ref="customers-filters-form"
          tag="form"
          class="search-form search-form-customers">

          <f-button
            v-if="filtersBtnVisible"
            type="button"
            theme="dark"
            class="block full-width mb-3"
            @click.prevent="toggleFilters">
            <template v-if="filtersOpen">
              {{ $t('advisersFinder.collapseFilters')}}
            </template>
            <template v-else>
              {{ $t('advisersFinder.expandFilters')}}
            </template>
          </f-button>

          <transition name="slide">
            <div v-if="!filtersBtnVisible || filtersOpen">
              <div class="flex search-form-top">
                <div class="search-form-inputs flex full-width">
                  <f-input-wrapper
                    prependIcon="search"
                    class="search search-field mb-1-ms">
                    <f-input
                      :placeholder="$t('ui.search')"
                      v-model="filters.search"/>
                  </f-input-wrapper>

                  <f-input
                    v-model="filters.city"
                    :placeholder="$t('user.city')"
                    class="ml-1 ml-0-ms city search-field"/>

                  <v-date-picker
                    v-model="lastActivityRange"
                    is-range
                    mode="dateTime"
                    is24hr
                    :locale="$i18n.locale"
                    class="date search-field">
                    <template v-slot="{ inputValue, togglePopover }">
                      <div class="flex justify-center items-center">
                        <input
                          :value="getDateRangeFormatted(inputValue)"
                          @click="togglePopover()"
                          :placeholder="$t('ui.recentActivity')"
                          class="form-control ml-1 full-width ml-0-sm mt-1-sm" />
                      </div>
                    </template>
                  </v-date-picker>
                </div>


                <div class="search-form-buttons flex">
                  <f-button
                    theme="primary"
                    class="ml-1 mb-1-ms ml-0-sm"
                    @click.prevent="validateFiltersForm">
                    {{ $t('ui.search')}}
                  </f-button>

                  <f-button
                    theme="warning"
                    class="ml-1"
                    @click.prevent="clearFilters">
                    {{ $t('advisersFinder.resetFilters')}}
                  </f-button>
                </div>
              </div>

              <div class="flex flex-wrap mt-2">
                <f-badge
                  v-for="(customerStatus, index) of customerStatuses"
                  :key="index"
                  class="mr-1 mb-2"
                  :theme="(customerStatus.value).toLowerCase().replaceAll('_', '-')"
                  :checkable="true">
                  <f-checkbox
                    v-model="filters.status[customerStatus.value]">
                    {{ customerStatus.text }}
                  </f-checkbox>
                </f-badge>
              </div>
              <div class="flex flex-align-items-center hidden-from-desktop-small-only">
                <label for="order" class="mb-0">{{ $t('advisersFinder.sort') }}:</label>
                <v-select
                  class="minimal ml-1 mobile-dropdown-align-left"
                  name="order"
                  :options="orderOptions"
                  v-model="order"
                  :reduce="(item) => { return { column: item.value, direction: item.direction } }"
                  :placeholder="$t('user.selectOrder')"
                  :clearable="false"
                  :searchable="false"
                  label="label">
                </v-select>
              </div>
            </div>
          </transition>
        </validation-observer>
      </div>
    </div>
    <div :class="{'wrapper mt-3': withFilters, 'is-loading': !loaded}">
      <f-list
        ref="all-customers-list"
        :class="{
          'customers-list': listField !== 'invited_count',
          'customers-list-less-info': listField === 'invited_count',
        }"
        :loaded="loaded"
        :data-mobile-actions-count="!withFilters ? 1 : 3"
        :listSettings="customerListSettings"
        :listItems="customersFormatted"
        :itemsSelectable="true"
        :bulkActions="customersBulkActions"
        :itemRoute="'/klienci/:id/'" />
    </div>

    <template v-if="hasPagination">
      <pagination
        :hasWrapper="true"
        :meta-data="paginationMetaData"
        :elementsCounterLabel="'customers'" />
    </template>

    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
        {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <div class="wrapper mt-3">
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import CustomerStatuses from '@/data/customer-statuses.js';

export default {
  name: 'customers-list',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    withFilters: {
      type: Boolean
    },
    hasPagination: {
      type: Boolean,
      default: true
    },
    listSource: {
      type: String
    },
    listField: {
      type: String
    },
    itemsList: {
      type: Array
    },
    whereFilter: {
      type: Object
    }
  },
  computed: {
    customerStatuses () {
      return CustomerStatuses;
    },
    customerListSettings () {
      let settingList =
      [
        {
          title: 'ID',
          variableName: ['id'],
          type: 'String',
          sortable: true,
          sortingEvent: 'customers-sort-toggle'
        },
        {
          title: this.$t('user.status'),
          variableName: ['customer_status'],
          type: 'f-badge'
        },
        {
          title: this.$t('user.fullName'),
          variableName: ['name', 'surname'],
          type: 'String'
        }
      ];

      if (this.listField !== 'invited_count') {
        settingList.push({
          title: this.$t('user.city'),
          variableName: ['city'],
          type: 'String'
        });
      } else {
        settingList.push({
          title: this.$t('user.emailAddress'),
          variableName: ['email'],
          type: 'String'
        });
      }

      settingList.push({
        title: this.$t('user.phone'),
        variableName: ['phone'],
        type: 'String'
      });

      if (this.listField !== 'invited_count') {
        settingList.push({
          title: this.$t('customersList.lastLogin'),
          variableName: ['last_login_at'],
          type: 'f-time-elapsed'
        });

        settingList.push({
          title: this.$t('customersList.lastInteraction'),
          variableName: ['last_activity_at'],
          type: 'f-time-elapsed'
        });

        settingList.push({
          title: '',
          variableName: ['inquiriesCounter'],
          type: 'f-message-counter',
          route: '/klienci/:id/lista-ofert'
        });

        settingList.push({
          title: '',
          variableName: ['messagesCounter'],
          type: 'f-message-counter',
          route: '/klienci/:id/lista-wiadomosci'
        });
      }

      settingList.push({
        title: '',
        variableName: ['actionsList'],
        type: 'actions',
        items: [
          {
            type: 'button',
            disabled: false,
            event: 'customer-edit',
            label: this.$t('ui.open'),
            name: 'edit'
          },
          {
            type: 'button',
            disabled: false,
            event: 'customer-invite',
            label: this.$t('ui.invite'),
            name: 'invite'
          },
          {
            type: 'button',
            disabled: false,
            event: 'customer-delete',
            label: this.$t('ui.remove'),
            theme: 'danger',
            name: 'delete'
          }
        ]
      });

      return settingList;
    },
    customersFormatted () {
      let customersList = this.customers;

      if (!customersList.length) {
        return [];
      }

      return customersList.map(item => {
        if (item.customer_status === 'DRAFT') {
          item.actionsList = ['edit', 'invite', 'delete'];
        } else {
          item.actionsList = ['edit', 'delete'];
        }

        if (this.listField !== 'invited_count') {
          if (item.customer_status === 'INVITATION_CONFIRMED') {
            item.inquiriesCounter = {
              counter: item.inquiries_count,
              type: 'inquiry',
              read: item.new_inquiries_count === 0
            }
            item.messagesCounter = {
              counter: item.message_topics_count,
              type: 'mail',
              read: item.new_message_topics_count === 0
            }
          } else {
            item.inquiriesCounter = {
              counter: 0,
              type: 'inquiry',
              disabled: true
            }
            item.messagesCounter = {
              counter: 0,
              type: 'mail',
              disabled: true
            }
          }
        }

        item.customer_status = {
          status: (item.customer_status).toLowerCase().replaceAll('_', '-'),
          text: this.customerStatuses.find(element => element.value === item.customer_status).text
        }

        return item;
      });
    },
    customersBulkActions () {
      return [{
        label: this.$t('customersList.deleteSelectCustomers'),
        callbackFunction: this.deleteSelected,
        theme: 'danger'
      }];
    }
  },
  watch: {
    'lastActivityRange.start': function (newValue) {
      if (newValue === null) {
        this.filters.last_activity_at_from = null;
      }

      this.filters.last_activity_at_from = this.formatDate(newValue);
    },
    'lastActivityRange.end': function (newValue) {
      if (newValue === null) {
        this.filters.last_activity_at_to = null;
      }

      this.filters.last_activity_at_to = this.formatDate(newValue);
    }
  },
  data() {
    return {
      userType: null,
      customers: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        city: '',
        status: {},
        last_activity_at_from: '',
        last_activity_at_to: '',
        contact_finhub_agreement: null,
        needs_answer: null
      },
      lastActivityRange: {
        start: null,
        end: null
      },
      order: {
        column: 'id',
        direction: 'ASC'
      },
      filtersOpen: false,
      filtersBtnVisible: false,
      orderOptions: [
        {
          label: this.$t('list.fromLatest'),
          value: 'id',
          direction: 'DESC'
        },
        {
          label: this.$t('list.fromOldest'),
          value: 'id',
          direction: 'ASC'
        }
      ]
    };
  },
  mounted () {
    this.userType = this.$store.getters['getUserType'].toLowerCase();
    this.$bus.$on('customer-edit', this.editCustomer);
    this.$bus.$on('customer-invite', this.inviteCustomer);
    this.$bus.$on('customer-delete', this.removeCustomer);
    this.$bus.$on('customers-sort-toggle', this.toggleSort);

    if (this.listField === 'invited_count') {
      Vue.set(this, 'customers', this.itemsList);
      this.loaded = true;
    } else {
      this.loadFilteredData();
      this.initFilterStatus();
    }
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let endpointParams = {};

      if (this.withFilters) {
        let where = {};

        if (this.whereFilter) {
          for (let i = 0; i < Object.keys(this.whereFilter).length; i++) {
            let key = Object.keys(this.whereFilter)[i];
            this.filters[key] = this.whereFilter[key];
          }
        }

        if (this.filters.search) {
          where.search = this.filters.search;
        }

        if (this.filters.city) {
          where.city = this.filters.city;
        }

        if (Object.keys(this.filters.status).filter(item => this.filters.status[item] === true)) {
          where.status = Object.keys(this.filters.status).filter(item => this.filters.status[item] === true);
        }

        if (this.filters.contact_finhub_agreement) {
          where.contact_finhub_agreement = this.filters.contact_finhub_agreement;
        }

        if (this.lastActivityRange.start && this.lastActivityRange.end) {
          where.last_activity_at_from = this.filters.last_activity_at_from;
          where.last_activity_at_to = this.filters.last_activity_at_to;
        }

        if (this.filters.needs_answer) {
          where.needs_answer = this.filters.needs_answer;
        }

        endpointParams.where = where;
        endpointParams.order = this.order
      }

      let enpointUrl = '/api/' + this.listSource;

      if (this.hasPagination) {
        enpointUrl += '?page=' + this.currentPage
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: enpointUrl,
        listField: 'customers',
        pagination: this.hasPagination,
        params: endpointParams
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        city: '',
        status: {},
        last_activity_at_from: '',
        last_activity_at_to: '',
        contact_finhub_agreement: null,
        needs_answer: null
      };

      this.lastActivityRange = {};

      this.order = {
        column: 'id',
        direction: 'ASC'
      }

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['customers-filters-form'], this.debouncedLoadFilteredData);
    },
    removeCustomer (customer) {
      this.removeInProgress = true;
      let customerId = typeof customer === 'object' ? customer.id : customer;

      ListUtils.removeItem(this, {
        endpointBase: '/api/' + this.userType + '/customers/delete/',
        id: customerId,
        successTitle: this.$t('customersList.deleteAccountCustomer'),
        successText: this.$t('customersList.deleteSelectAccountCustomer'),
        confirmTitle: this.$t('customersList.doYouWantDeleteAccountCustomer'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('customersList.deleteAccountCustomerError'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    formatDate (timestamp) {
      if (!timestamp) {
        return this.$t('ui.none').toLowerCase();
      }

      let dateObject = new Date(timestamp);

      return [
        dateObject.getFullYear(),
        dateObject.getMonth() + 1,
        dateObject.getDate()
      ].map(n => n < 10 ? '0' + n : n).join('-') + ' ' + [
        dateObject.getHours(),
        dateObject.getMinutes()
      ].map(n => n < 10 ? '0' + n : n).join(':');
    },
    getDateRangeFormatted (dateRange) {
      if (!dateRange || (!dateRange.start && !dateRange.end)) {
        return null;
      }

      let dateRangeStart = dateRange.start;
      let dateRangeEnd = dateRange.end;

      if (!dateRangeStart) {
        dateRangeStart = 'od';
      }
      if (!dateRangeEnd) {
        dateRangeEnd = 'do';
      }

      return dateRangeStart + ' - ' + dateRangeEnd;
    },
    toggleSort (param) {
      this.order.column = param.columnName[0];
      this.order.direction = this.order.direction === 'ASC' ? 'DESC' : 'ASC';
      this.loadFilteredData();
    },
    deleteSelected (items) {
      this.removeInProgress = true;
      let itemsToRemove = Object.keys(items).filter(key => items[key] === true);

      ListUtils.bulkActionItems(this, {
        endpoint: '/api/adviser/customers/delete-many',
        successTitle: this.$t('customersList.deletedCustomers'),
        successText: this.$t('customersList.selectedCustomersHaveBeenRemoved'),
        confirmTitle: this.$t('customersList.doYouWantDeleteAccountCustomers'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('customersList.deleteAccountCustomersError'),
        items: itemsToRemove,
        successAction: () => {
          this.$refs['all-customers-list'].deselectAllItems();
          this.removeInProgress = false;
          this.$bus.$emit('pagination-reset');
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    initFilterStatus() {
      let tmpStatusfilter = {};
      for (let i = 0; i < this.customerStatuses.length; i++) {
        tmpStatusfilter[this.customerStatuses[i].value] = false;
      }
      Vue.set(this.filters, 'status', tmpStatusfilter);
    },
    editCustomer (customer) {
      this.$router.push('/klienci/' + customer.id);
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    },
    inviteCustomer (customer) {
      FormUtils.submitWithConfirm(this, {
        endpoint: '/api/adviser/customers/send-invitation/' + customer.id,
        successTitle: this.$t('customersList.inviteCustomer'),
        successText: this.$t('customersList.invitationSentToCustomer'),
        confirmTitle: this.$t('customersList.invitationSentToCustomer'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        confirmButtonText: this.$t('ui.yesInvite'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('customersList.sendInvitationError'),
        successAction: () => {
          this.loadFilteredData();
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('customer-edit', this.editCustomer);
    this.$bus.$off('customer-invite', this.inviteCustomer);
    this.$bus.$off('customer-delete', this.removeCustomer);
    this.$bus.$off('customers-sort-toggle', this.toggleSort);
  }
};
</script>

